/**
 * The map surface: pan, zoom, hit-testing and level-of-detail.
 *
 * Rendering is plain SVG driven by React, with d3-zoom owning only the
 * transform. That split matters: d3 never touches the DOM React manages, so
 * there is no reconciliation fight, and the layout stays a pure function we can
 * test without a browser.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { select } from 'd3-selection';
// Imported for its side effect: it teaches d3 selections the .transition() method.
import 'd3-transition';
import { zoom, zoomIdentity, type D3ZoomEvent, type ZoomBehavior } from 'd3-zoom';
import type { Snapshot } from '@proxmon/shared';
import { computeLayout, type MapLayout, type MapNode } from './layout.ts';
import { BridgeGlyph, GuestGlyph, HostGlyph, type GlyphState } from './glyphs.tsx';
import { useStore } from '../state/store.ts';

const MIN_SCALE = 0.08;
const MAX_SCALE = 4;
/** below this scale, guest names are unreadable clutter */
const LABEL_SCALE = 0.42;
const DETAIL_SCALE = 0.75;

interface Props {
  snapshot: Snapshot;
}

export function MapCanvas({ snapshot }: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [transform, setTransform] = useState(() => zoomIdentity);
  const [size, setSize] = useState({ width: 1200, height: 800 });
  const [grabbing, setGrabbing] = useState(false);

  const filters = useStore((s) => s.filters);
  const search = useStore((s) => s.search);
  const selectedId = useStore((s) => s.selectedId);
  const hoveredId = useStore((s) => s.hoveredId);
  const fitRequest = useStore((s) => s.fitRequest);
  const selectNode = useStore((s) => s.select);
  const hoverNode = useStore((s) => s.hover);

  const layout: MapLayout = useMemo(
    () =>
      computeLayout(snapshot, {
        hideStopped: filters.hideStopped,
        hideTemplates: filters.hideTemplates,
        showBridges: filters.showBridges,
      }),
    // Each of these changes the layout itself, not just the paint.
    [snapshot, filters.hideStopped, filters.hideTemplates, filters.showBridges],
  );

  /** ids matching the current search; null when there is no search */
  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return null;
    const found = new Set<string>();
    for (const node of layout.nodes) {
      if (matchesQuery(node, query)) found.add(node.id);
    }
    return found;
  }, [layout, search]);

  const fitToBounds = useCallback(
    (animate: boolean) => {
      const svg = svgRef.current;
      const behavior = zoomRef.current;
      if (!svg || !behavior) return;

      const { minX, minY, maxX, maxY } = layout.bounds;
      const width = Math.max(1, maxX - minX);
      const height = Math.max(1, maxY - minY);
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.min(size.width / width, size.height / height)));
      const next = zoomIdentity
        .translate(size.width / 2, size.height / 2)
        .scale(scale)
        .translate(-(minX + maxX) / 2, -(minY + maxY) / 2);

      const selection = select(svg);
      if (animate) selection.transition().duration(420).call(behavior.transform, next);
      else selection.call(behavior.transform, next);
    },
    [layout.bounds, size.height, size.width],
  );

  // --- wire up d3-zoom once -------------------------------------------------
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const behavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([MIN_SCALE, MAX_SCALE])
      .filter((event: Event) => {
        // Let modifier-clicks through to the browser, and ignore right-drag.
        if (event.type === 'wheel') return true;
        const mouse = event as MouseEvent;
        return !mouse.ctrlKey && mouse.button === 0;
      })
      .on('start', () => setGrabbing(true))
      .on('zoom', (event: D3ZoomEvent<SVGSVGElement, unknown>) => setTransform(event.transform))
      .on('end', () => setGrabbing(false));

    zoomRef.current = behavior;
    select(svg).call(behavior).on('dblclick.zoom', null);

    return () => {
      select(svg).on('.zoom', null);
      zoomRef.current = null;
    };
  }, []);

  // --- keep the viewport in step with the element --------------------------
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 0 && rect.height > 0) setSize({ width: rect.width, height: rect.height });
    });
    observer.observe(svg);
    return () => observer.disconnect();
  }, []);

  /**
   * Fit once, when the first real layout for a cluster arrives. Deliberately a
   * ref and not the node count: a guest starting or stopping changes the count,
   * and yanking the viewport out from under someone mid-inspection is worse
   * than leaving the new node off-screen.
   */
  const hasFitted = useRef(false);
  useEffect(() => {
    if (hasFitted.current || layout.nodes.length === 0) return;
    hasFitted.current = true;
    fitToBounds(false);
  }, [fitToBounds, layout.nodes.length]);

  /**
   * Fit on demand. fitRequest is a monotonic counter, so we latch the value we
   * last acted on: fitToBounds changes identity with every snapshot (new bounds
   * object), and without the latch that re-fires the fit on every poll.
   */
  const lastFitRequest = useRef(0);
  useEffect(() => {
    if (fitRequest === lastFitRequest.current) return;
    lastFitRequest.current = fitRequest;
    fitToBounds(true);
  }, [fitRequest, fitToBounds]);

  const zoomBy = useCallback((factor: number) => {
    const svg = svgRef.current;
    const behavior = zoomRef.current;
    if (!svg || !behavior) return;
    select(svg).transition().duration(180).call(behavior.scaleBy, factor);
  }, []);

  /** Centre on a node without changing the zoom level. */
  const centerOn = useCallback(
    (node: MapNode) => {
      const svg = svgRef.current;
      const behavior = zoomRef.current;
      if (!svg || !behavior) return;
      const scale = Math.max(transform.k, 0.8);
      const next = zoomIdentity
        .translate(size.width / 2, size.height / 2)
        .scale(scale)
        .translate(-node.x, -node.y);
      select(svg).transition().duration(360).call(behavior.transform, next);
    },
    [size.height, size.width, transform.k],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
      if (event.key === 'Escape') selectNode(null);
      else if (event.key === '+' || event.key === '=') zoomBy(1.3);
      else if (event.key === '-' || event.key === '_') zoomBy(1 / 1.3);
      else if (event.key === 'f') fitToBounds(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fitToBounds, selectNode, zoomBy]);

  const showLabels = transform.k >= LABEL_SCALE;
  const showDetail = transform.k >= DETAIL_SCALE;

  const stateFor = (node: MapNode): GlyphState => ({
    selected: selectedId === node.id,
    hovered: hoveredId === node.id,
    dimmed: matches !== null && !matches.has(node.id),
    // Hosts and bridges keep their labels much further out than guests do.
    showLabel: node.kind === 'guest' ? showLabels : transform.k >= 0.2,
    showDetail,
  });

  return (
    <>
      <svg
        ref={svgRef}
        className={`map${grabbing ? ' grabbing' : ''}`}
        onPointerDown={(event) => {
          if (event.target === svgRef.current) selectNode(null);
        }}
      >
        <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
          <g className="links">
            {layout.links.map((link) => {
              const dimmed = matches !== null && !matches.has(link.sourceId) && !matches.has(link.targetId);
              return (
                <path
                  key={link.id}
                  className="link"
                  d={elbow(link.x1, link.y1, link.x2, link.y2)}
                  stroke={link.edge ? '#7a5a1f' : undefined}
                  strokeWidth={link.kind === 'secondary' ? 1 : link.edge ? 1.8 : 1.4}
                  strokeDasharray={link.kind === 'secondary' ? '4 4' : undefined}
                  opacity={dimmed ? 0.12 : link.kind === 'secondary' ? 0.5 : 0.85}
                />
              );
            })}
          </g>

          {/* When bridge nodes are collapsed the segment name rides on the link,
              so the topology stays readable without them. */}
          {showDetail && (
            <g className="link-labels">
              {layout.links
                .filter((link) => link.kind === 'primary' && link.viaBridge)
                .map((link) => (
                  <text
                    key={`lbl:${link.id}`}
                    className="link-label"
                    x={(link.x1 + link.x2) / 2}
                    y={(link.y1 + link.y2) / 2 - 4}
                  >
                    {link.viaBridge}
                  </text>
                ))}
            </g>
          )}

          <g className="nodes">
            {layout.nodes.map((node) => (
              <g
                key={node.id}
                onPointerEnter={() => hoverNode(node.id)}
                onPointerLeave={() => hoverNode(null)}
                onClick={(event) => {
                  event.stopPropagation();
                  selectNode(node.id);
                }}
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  centerOn(node);
                }}
              >
                {node.kind === 'host' && <HostGlyph node={node} state={stateFor(node)} />}
                {node.kind === 'bridge' && <BridgeGlyph node={node} state={stateFor(node)} />}
                {node.kind === 'guest' && <GuestGlyph node={node} state={stateFor(node)} />}
              </g>
            ))}
          </g>
        </g>
      </svg>

      <div className="controls">
        <button type="button" onClick={() => zoomBy(1.3)} title="Zoom in (+)" aria-label="Zoom in">
          +
        </button>
        <button type="button" onClick={() => zoomBy(1 / 1.3)} title="Zoom out (-)" aria-label="Zoom out">
          −
        </button>
        <button type="button" onClick={() => fitToBounds(true)} title="Fit to view (f)" aria-label="Fit to view">
          ⤢
        </button>
      </div>

      {layout.nodes.length === 0 && (
        <div className="center-note">No hosts to draw yet.</div>
      )}
    </>
  );
}

/**
 * A horizontal cubic bezier: leaves the parent going right, arrives at the
 * child going right. Straight lines in a left-to-right tree make every sibling
 * fan look like a starburst; these read as cabling.
 */
function elbow(x1: number, y1: number, x2: number, y2: number): string {
  const mx = (x1 + x2) / 2;
  return `M ${x1.toFixed(1)} ${y1.toFixed(1)} C ${mx.toFixed(1)} ${y1.toFixed(1)}, ${mx.toFixed(1)} ${y2.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
}

function matchesQuery(node: MapNode, query: string): boolean {
  if (node.label.toLowerCase().includes(query)) return true;
  if (node.host.toLowerCase().includes(query)) return true;
  if (node.kind === 'guest') {
    const guest = node.data;
    if (String(guest.vmid).includes(query)) return true;
    if (guest.tags.some((tag) => tag.toLowerCase().includes(query))) return true;
    if (guest.nics.some((nic) => nic.mac?.includes(query) || nic.ip?.address.includes(query))) return true;
    if (guest.kind.includes(query)) return true;
  }
  if (node.kind === 'bridge' && node.data.cidr?.includes(query)) return true;
  return false;
}
